import React, { useState, useRef, useEffect } from "react";
import {
  MenuSwitcherButton,
  IconEnFlag,
  IconFrCaFlag,
  Menu,
  MenuListItem,
  MENU_PLACEMENT,
} from "@cdkglobal/radial";
import { useLocaleContext } from "./contexts/LocaleContext";
import i18n from "./translate/i18next";

interface SmartlingDropdownProps {
  isCollapsed: boolean;
}

export const SmartlingDropdown: React.FC<SmartlingDropdownProps> = ({
  isCollapsed,
}) => {
  const { locale, setLocale } = useLocaleContext();

  const options = [
    {
      id: "language-en-US",
      icon: <IconEnFlag />,
      label: "EN",
      value: "en-US",
    },
    {
      id: "language-fr-CA",
      icon: <IconFrCaFlag />,
      label: "FR",
      value: "fr-CA",
    },
  ];

  const selectedOption =
    options.find((opt) => opt.value === locale) ?? options[0];

  const [isOpen, setIsOpen] = useState(false);
  const targetRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (locale) {
      const normalizedLocale = locale.startsWith("fr") ? "fr" : "en";
      i18n.changeLanguage(normalizedLocale);
    }
  }, [locale]);

  const handleSelect = (optionValue: string) => {
    setLocale(optionValue);
    setIsOpen(false);
    targetRef.current?.focus();
  };

  const handleClose = () => {
    setIsOpen(false);
    targetRef.current?.focus();
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  useEffect(() => {
    const setIsOpenFromMenu = (event: CustomEvent) => {
      if (event.detail.ref === menuRef) {
        setIsOpen(event.detail.openMenu);
      }
    };
    document.addEventListener(
      "customMenuOpenEvent",
      setIsOpenFromMenu as EventListener,
    );
    return () => {
      document.removeEventListener(
        "customMenuOpenEvent",
        setIsOpenFromMenu as EventListener,
      );
    };
  }, []);

  if (isCollapsed) {
    return selectedOption.icon;
  }

  return (
    <>
      <MenuSwitcherButton
        dataTestId="smartling-language-switcher-button"
        icon={selectedOption.icon}
        isOpen={isOpen}
        label={isCollapsed ? undefined : selectedOption.label}
        ref={targetRef}
      />
      <Menu
        aria-label="Select Language"
        placement={MENU_PLACEMENT.BOTTOM_START}
        labelRef={targetRef}
        onClose={handleClose}
        onOpen={handleOpen}
        isOpen={isOpen}
        ref={menuRef}
      >
        {options.map((option, index) => (
          <MenuListItem
            title={option.label}
            key={option.id}
            dataTestId={`smartling-language-option-${index}`}
            isSelected={locale === option.value}
            onClick={() => handleSelect(option.value)}
            graphicContent={option.icon}
            hideSelectedItemCheckmark={false}
          >
            {option.label}
          </MenuListItem>
        ))}
      </Menu>
    </>
  );
};
